import { useState } from "react";
import { auth } from "./firebase";
import {
  updatePassword,
  updateEmail,
  deleteUser,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";

export default function Settings({ user, onBack, language = "en", onLanguageChange = () => {}, onShowPremium }) {

  const [activeSection, setActiveSection] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [audioQuality, setAudioQuality] = useState(localStorage.getItem("settings_quality") || "high");
  const [autoplay, setAutoplay] = useState(localStorage.getItem("settings_autoplay") !== "false");
  const [crossfade, setCrossfade] = useState(localStorage.getItem("settings_crossfade") === "true");
  const [showExplicit, setShowExplicit] = useState(localStorage.getItem("settings_explicit") !== "false");
  const [privateAccount, setPrivateAccount] = useState(localStorage.getItem("settings_private") === "true");
  const [showActivity, setShowActivity] = useState(localStorage.getItem("settings_activity") !== "false");
  const [allowMessages, setAllowMessages] = useState(localStorage.getItem("settings_messages") !== "false");
  const [notifLikes, setNotifLikes] = useState(localStorage.getItem("settings_notif_likes") !== "false");
  const [notifComments, setNotifComments] = useState(localStorage.getItem("settings_notif_comments") !== "false");
  const [notifFollowers, setNotifFollowers] = useState(localStorage.getItem("settings_notif_followers") !== "false");
  const [notifMessages, setNotifMessages] = useState(localStorage.getItem("settings_notif_messages") !== "false");

  function saveSetting(key, value) {
    localStorage.setItem(`settings_${key}`, value);
  }

  function Toggle({ value, onChange }) {
    return (
      <div onClick={() => onChange(!value)} style={{ width: 44, height: 24, background: value ? "#1db954" : "#333", borderRadius: 12, position: "relative", cursor: "pointer", flexShrink: 0, transition: "background 0.2s" }}>
        <div style={{ position: "absolute", top: 2, left: value ? 22 : 2, width: 20, height: 20, background: "#fff", borderRadius: "50%", transition: "left 0.2s" }} />
      </div>
    );
  }

  async function reauth() {
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(auth.currentUser, credential);
  }

  async function handleUpdateEmail() {
    if (!newEmail) { setError("Enter a new email."); return; }
    if (!currentPassword) { setError("Enter your current password."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      await reauth();
      await updateEmail(auth.currentUser, newEmail);
      setSuccess("Email updated!");
      setNewEmail(""); setCurrentPassword("");
    } catch (e) {
      setError(e.code === "auth/wrong-password" ? "Wrong password." : "Failed to update email.");
    }
    setLoading(false);
  }

  async function handleUpdatePassword() {
    if (!newPassword) { setError("Enter a new password."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords don't match."); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (!currentPassword) { setError("Enter your current password."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      await reauth();
      await updatePassword(auth.currentUser, newPassword);
      setSuccess("Password updated!");
      setNewPassword(""); setConfirmPassword(""); setCurrentPassword("");
    } catch (e) {
      setError(e.code === "auth/wrong-password" ? "Wrong password." : "Failed to update password.");
    }
    setLoading(false);
  }

  async function handleDeleteAccount() {
    if (!currentPassword) { setError("Enter your password to confirm."); return; }
    setLoading(true); setError("");
    try {
      await reauth();
      await deleteUser(auth.currentUser);
    } catch (e) {
      setError(e.code === "auth/wrong-password" ? "Wrong password." : "Failed to delete account.");
    }
    setLoading(false);
  }

  const S = {
    page: { position: "fixed", inset: 0, zIndex: 999, background: "#080808", color: "#fff", fontFamily: "'DM Sans',sans-serif", overflowY: "auto", paddingBottom: 60 },
    header: { background: "rgba(0,0,0,0.9)", padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid #111", position: "sticky", top: 0, zIndex: 10, backdropFilter: "blur(10px)" },
    section: { background: "#111", borderRadius: 14, overflow: "hidden", marginBottom: 12 },
    row: { padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid #1a1a1a", cursor: "pointer" },
    rowLast: { padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" },
    label: { flex: 1, fontSize: 14, color: "#fff" },
    arrow: { color: "#444", fontSize: 16 },
    groupTitle: { fontSize: 11, color: "#444", fontWeight: 700, letterSpacing: "0.1em", margin: "16px 0 8px", textTransform: "uppercase" },
    input: { width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #2a2a2a", background: "#0a0a0a", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", marginBottom: 10, boxSizing: "border-box" },
    btn: { width: "100%", padding: 14, borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer", marginBottom: 10 },
  };

  // Section detail view
  if (activeSection === "email") return (
    <div style={S.page}>
      <div style={S.header}>
        <button onClick={() => { setActiveSection(null); setError(""); setSuccess(""); }} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}>←</button>
        <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800 }}>Change Email</span>
      </div>
      <div style={{ padding: 20 }}>
        {error && <div style={{ background: "rgba(233,20,41,0.1)", border: "1px solid #e91429", borderRadius: 10, padding: 12, fontSize: 13, color: "#e91429", marginBottom: 14 }}>{error}</div>}
        {success && <div style={{ background: "rgba(29,185,84,0.1)", border: "1px solid #1db954", borderRadius: 10, padding: 12, fontSize: 13, color: "#1db954", marginBottom: 14 }}>{success}</div>}
        <input style={S.input} placeholder="New email" value={newEmail} onChange={e => setNewEmail(e.target.value)} type="email" />
        <input style={S.input} placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} type="password" />
        <button style={S.btn} onClick={handleUpdateEmail} disabled={loading}>{loading ? "Updating..." : "Update Email"}</button>
      </div>
    </div>
  );

  if (activeSection === "password") return (
    <div style={S.page}>
      <div style={S.header}>
        <button onClick={() => { setActiveSection(null); setError(""); setSuccess(""); }} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}>←</button>
        <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800 }}>Change Password</span>
      </div>
      <div style={{ padding: 20 }}>
        {error && <div style={{ background: "rgba(233,20,41,0.1)", border: "1px solid #e91429", borderRadius: 10, padding: 12, fontSize: 13, color: "#e91429", marginBottom: 14 }}>{error}</div>}
        {success && <div style={{ background: "rgba(29,185,84,0.1)", border: "1px solid #1db954", borderRadius: 10, padding: 12, fontSize: 13, color: "#1db954", marginBottom: 14 }}>{success}</div>}
        <input style={S.input} placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} type="password" />
        <input style={S.input} placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" />
        <input style={S.input} placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password" />
        <button style={S.btn} onClick={handleUpdatePassword} disabled={loading}>{loading ? "Updating..." : "Update Password"}</button>
      </div>
    </div>
  );

  if (activeSection === "delete") return (
    <div style={S.page}>
      <div style={S.header}>
        <button onClick={() => { setActiveSection(null); setError(""); }} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}>←</button>
        <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 800 }}>Delete Account</span>
      </div>
      <div style={{ padding: 20 }}>
        <div style={{ background: "rgba(233,20,41,0.1)", border: "1px solid #e91429", borderRadius: 14, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#e91429", marginBottom: 8 }}>⚠️ This cannot be undone</div>
          <div style={{ fontSize: 13, color: "#aaa", lineHeight: 1.6 }}>Deleting your account will permanently remove all your data, songs, messages and profile from MyMusic.</div>
        </div>
        {error && <div style={{ background: "rgba(233,20,41,0.1)", border: "1px solid #e91429", borderRadius: 10, padding: 12, fontSize: 13, color: "#e91429", marginBottom: 14 }}>{error}</div>}
        <input style={S.input} placeholder="Enter your password to confirm" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} type="password" />
        <button onClick={handleDeleteAccount} disabled={loading} style={{ ...S.btn, background: "#e91429", color: "#fff" }}>{loading ? "Deleting..." : "Delete My Account"}</button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>
      
      {/* Header */}
      <div style={S.header}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}>←</button>
        <span style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800 }}>Settings</span>
      </div>

      <div style={{ padding: "16px 16px 0" }}>

        {/* Profile Card */}
        <div style={{ background: "#111", borderRadius: 16, padding: 16, display: "flex", alignItems: "center", gap: 12, marginBottom: 20, border: "1px solid #1a1a1a" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#222", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {user.photoURL ? <img src={user.photoURL} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 22 }}>👤</span>}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.displayName || user.email?.split("@")[0]}</div>
            <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{user.email}</div>
          </div>
          {onShowPremium && <button onClick={onShowPremium} style={{ background: "linear-gradient(135deg, #1db954, #0a8f3c)", color: "#000", fontSize: 11, fontWeight: 700, padding: "6px 12px", borderRadius: 50, border: "none", cursor: "pointer", flexShrink: 0 }}>⭐ Upgrade</button>}
        </div>

        {/* Account */}
        <div style={S.groupTitle}>Account</div>
        <div style={S.section}>
          <div style={S.row} onClick={() => setActiveSection("email")}>
            <span style={{ fontSize: 18 }}>📧</span><span style={S.label}>Change Email</span><span style={S.arrow}>›</span>
          </div>
          <div style={S.row} onClick={() => setActiveSection("password")}>
            <span style={{ fontSize: 18 }}>🔑</span><span style={S.label}>Change Password</span><span style={S.arrow}>›</span>
          </div>
          <div style={S.rowLast} onClick={() => setActiveSection("delete")}>
            <span style={{ fontSize: 18 }}>🗑️</span><span style={{ ...S.label, color: "#e91429" }}>Delete Account</span><span style={S.arrow}>›</span>
          </div>
        </div>

        {/* Music */}
        <div style={S.groupTitle}>Music</div>
        <div style={S.section}>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>🎵</span>
            <span style={S.label}>Audio Quality</span>
            <select value={audioQuality} onChange={e => { setAudioQuality(e.target.value); saveSetting("quality", e.target.value); }} style={{ background: "#222", color: "#fff", border: "none", borderRadius: 8, padding: "4px 8px", fontSize: 13, cursor: "pointer" }}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>⏭️</span><span style={S.label}>Autoplay</span>
            <Toggle value={autoplay} onChange={v => { setAutoplay(v); saveSetting("autoplay", v); }} />
          </div>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>🔀</span><span style={S.label}>Crossfade</span>
            <Toggle value={crossfade} onChange={v => { setCrossfade(v); saveSetting("crossfade", v); }} />
          </div>
          <div style={S.rowLast}>
            <span style={{ fontSize: 18 }}>🔞</span><span style={S.label}>Show Explicit Content</span>
            <Toggle value={showExplicit} onChange={v => { setShowExplicit(v); saveSetting("explicit", v); }} />
          </div>
        </div>

        {/* Social */}
        <div style={S.groupTitle}>Social</div>
        <div style={S.section}>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>🔒</span><span style={S.label}>Private Account</span>
            <Toggle value={privateAccount} onChange={v => { setPrivateAccount(v); saveSetting("private", v); }} />
          </div>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>👁️</span><span style={S.label}>Show Listening Activity</span>
            <Toggle value={showActivity} onChange={v => { setShowActivity(v); saveSetting("activity", v); }} />
          </div>
          <div style={S.rowLast}>
            <span style={{ fontSize: 18 }}>✉️</span><span style={S.label}>Allow Messages from Anyone</span>
            <Toggle value={allowMessages} onChange={v => { setAllowMessages(v); saveSetting("messages", v); }} />
          </div>
        </div>

        {/* Notifications */}
        <div style={S.groupTitle}>Notifications</div>
        <div style={S.section}>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>❤️</span><span style={S.label}>Likes</span>
            <Toggle value={notifLikes} onChange={v => { setNotifLikes(v); saveSetting("notif_likes", v); }} />
          </div>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>💬</span><span style={S.label}>Comments</span>
            <Toggle value={notifComments} onChange={v => { setNotifComments(v); saveSetting("notif_comments", v); }} />
          </div>
          <div style={S.row}>
            <span style={{ fontSize: 18 }}>👥</span><span style={S.label}>New Followers</span>
            <Toggle value={notifFollowers} onChange={v => { setNotifFollowers(v); saveSetting("notif_followers", v); }} />
          </div>
          <div style={S.rowLast}>
            <span style={{ fontSize: 18 }}>✉️</span><span style={S.label}>Messages</span>
            <Toggle value={notifMessages} onChange={v => { setNotifMessages(v); saveSetting("notif_messages", v); }} />
          </div>
        </div>

        {/* Language */}
        <div style={S.groupTitle}>Language</div>
        <div style={S.section}>
          <div style={S.rowLast}>
            <span style={{ fontSize: 18 }}>🌍</span>
            <span style={S.label}>App Language</span>
            <select value={language} onChange={e => onLanguageChange(e.target.value)} style={{ background: "#222", color: "#fff", border: "none", borderRadius: 8, padding: "4px 8px", fontSize: 13, cursor: "pointer" }}>
              <option value="en">English</option>
              <option value="my">မြန်မာ</option>
              <option value="ko">한국어</option>
              <option value="hi">हिंदी</option>
              <option value="zh">中文</option>
            </select>
          </div>
        </div>

        {/* Support */}
        <div style={S.groupTitle}>Support</div>
        <div style={S.section}>
          <div style={S.row} onClick={() => window.open("mailto:support@mymusicstream.online")}>
            <span style={{ fontSize: 18 }}>💬</span><span style={S.label}>Contact Support</span><span style={S.arrow}>›</span>
          </div>
          <div style={S.row} onClick={() => window.open("https://mymusicstream.online/#privacy")}>
            <span style={{ fontSize: 18 }}>🔒</span><span style={S.label}>Privacy Policy</span><span style={S.arrow}>›</span>
          </div>
          <div style={S.rowLast} onClick={() => window.open("https://mymusicstream.online/#terms")}>
            <span style={{ fontSize: 18 }}>📜</span><span style={S.label}>Terms of Service</span><span style={S.arrow}>›</span>
          </div>
        </div>

        {/* Sign Out */}
        <button onClick={() => signOut(auth)} style={{ width: "100%", padding: 14, borderRadius: 50, border: "1px solid #e91429", background: "transparent", color: "#e91429", fontSize: 14, fontWeight: 700, cursor: "pointer", margin: "8px 0 16px" }}>
          Sign Out
        </button>

        <div style={{ textAlign: "center", fontSize: 11, color: "#333", paddingBottom: 20 }}>
          MyMusic v1.0 · mymusicstream.online
        </div>

      </div>
    </div>
  );
}